import React, { useState, useEffect } from 'react';
import { CATEGORIES, SUBCATEGORIES, CATEGORY_MAP } from '../../constants/categories';
import { EVENT_TYPES, SERVICE_CATEGORIES, SERVICE_SUBCATEGORIES, SERVICE_DURATIONS } from '../../constants/eventTypes';
import { RADIUS_OPTIONS } from '../../constants/zipData';
import ZipInput from '../shared/ZipInput';
import CheckboxGroup from '../shared/CheckboxGroup';
import CategorySubcategoryPicker from '../shared/CategorySubcategoryPicker';
import PasswordInput from '../shared/PasswordInput';
import UploadZone from '../shared/UploadZone';
import TosModal from '../modals/TosModal';

const VENDOR_DRAFT_KEY      = 'sjvm_vendor_draft';
const VENDOR_DRAFT_SUBS_KEY = 'sjvm_vendor_draft_subs';

const DEFAULT_VENDOR_FORM = {
  businessName:'', ownerName:'', email:'', phone:'',
  homeZip:'', radius:20,
  vendorType:{market:false, service:false},
  categories:[], subcategories:[],
  serviceCategories:[], serviceSubcategories:[], serviceOtherText:{},
  description:'', website:'', facebook:'', instagram:'', tiktok:'', youtube:'', otherSocial:'',
  eventTypes:[],
  insurance:false,
  hasMinPurchase:false, minPurchaseAmt:25,
  chargesPrivateFee:false, privateEventFee:150,
  priceMax:0,
  otherCategory:'', otherEventType:'',
  responseTime:'24hrs', bookingLeadTime:'2weeks', eventFrequency:'flexible', emailFrequency:'instant',
  setupTime:30, tableSize:'6ft', needsElectric:false,
  yearsActive:'', password:'',
  isServiceProvider:false, serviceType:'', serviceRateMin:'', serviceRateMax:'', serviceRateType:'fixed', minBookingDuration:'1 hour', serviceDescription:'',
  referralCode:''
};

function VendorForm({ onSubmit, setTab, authUser, setShowAuthModal, openSignup, defaultReferralCode }) {
  const [formStep, setFormStep] = useState(1);
  const STEPS = [{n:1,label:'Business Info'},{n:2,label:'Categories'},{n:3,label:'Photos & Links'},{n:4,label:'Review & Submit'}];
  const [tosAgreed, setTosAgreed] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [coiFile, setCoiFile] = useState(null);
  const [lookbookFile, setLookbookFile] = useState(null);
  const [hasDraft] = useState(() => !!localStorage.getItem(VENDOR_DRAFT_KEY));
  const [otherSubcategories, setOtherSubcategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem(VENDOR_DRAFT_SUBS_KEY) || '{}'); }
    catch { return {}; }
  });
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(VENDOR_DRAFT_KEY);
      if (!saved) return DEFAULT_VENDOR_FORM;
      const draft = { ...DEFAULT_VENDOR_FORM, ...JSON.parse(saved) };
      if (draft.categories) draft.categories = [...new Set(draft.categories.map(c => CATEGORY_MAP[c] || c).filter(c => CATEGORIES.includes(c)))];
      if (draft.subcategories) {
        const validSubs = new Set((draft.categories||[]).flatMap(c => SUBCATEGORIES[c] || []));
        draft.subcategories = draft.subcategories.filter(s => validSubs.has(s));
      }
      return draft;
    } catch { return DEFAULT_VENDOR_FORM; }
  });
  useEffect(() => { const { password: _pw, ...draft } = form; localStorage.setItem(VENDOR_DRAFT_KEY, JSON.stringify(draft)); }, [form]);
  useEffect(() => { localStorage.setItem(VENDOR_DRAFT_SUBS_KEY, JSON.stringify(otherSubcategories)); }, [otherSubcategories]);
  const clearDraft = () => {
    localStorage.removeItem(VENDOR_DRAFT_KEY);
    localStorage.removeItem(VENDOR_DRAFT_SUBS_KEY);
    setForm(DEFAULT_VENDOR_FORM);
    setOtherSubcategories({});
  };
  useEffect(() => { if (defaultReferralCode && !form.referralCode) set('referralCode', defaultReferralCode); }, [defaultReferralCode]); // eslint-disable-line
  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const nextStep = () => { setFormStep(s => Math.min(s+1, 4)); window.scrollTo({top:0,behavior:'smooth'}); };
  const prevStep = () => { setFormStep(s => Math.max(s-1, 1)); window.scrollTo({top:0,behavior:'smooth'}); };
  return (
    <div className="form-card">
      {/* Step progress bar */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:0,marginBottom:24}}>
        {STEPS.map((s,i) => (
          <div key={s.n} style={{display:'flex',alignItems:'center'}}>
            <div onClick={()=>{setFormStep(s.n);window.scrollTo({top:0,behavior:'smooth'});}} style={{display:'flex',flexDirection:'column',alignItems:'center',cursor:'pointer',minWidth:70}}>
              <div style={{width:28,height:28,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,
                background:formStep===s.n?'#c8a84b':formStep>s.n?'#1a6b3a':'#e8ddd0',
                color:formStep===s.n?'#1a1410':formStep>s.n?'#fff':'#a89a8a'}}>{formStep>s.n?'✓':s.n}</div>
              <div style={{fontSize:10,color:formStep===s.n?'#1a1410':'#a89a8a',fontWeight:formStep===s.n?700:400,marginTop:4}}>{s.label}</div>
            </div>
            {i<STEPS.length-1 && <div style={{width:40,height:2,background:formStep>s.n?'#1a6b3a':'#e8ddd0',margin:'0 4px 16px'}}/>}
          </div>
        ))}
      </div>
      {hasDraft && (
        <div style={{ background:'#fdf4dc', border:'1px solid #ffd966', borderRadius:8, padding:'12px 16px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div style={{ fontSize:14, color:'#7a5a10' }}>📋 <strong>Draft restored</strong> — your previously entered information has been loaded.</div>
          <button onClick={clearDraft} style={{ background:'none', border:'1px solid #c8a84b', color:'#7a5a10', borderRadius:6, padding:'5px 14px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'DM Sans,sans-serif', whiteSpace:'nowrap' }}>Clear &amp; Start Over</button>
        </div>
      )}
      {/* ── STEP 1: Business Info ── */}
      {formStep === 1 && (<>
      {!authUser && (
        <>
          <div style={{display:'flex',gap:10,marginBottom:20}}>
            <button onClick={()=>{if(typeof setShowAuthModal==='function')setShowAuthModal(true);}} style={{flex:1,background:'#1a1410',color:'#e8c97a',border:'none',borderRadius:8,padding:'14px 0',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Log In</button>
            <button onClick={()=>{if(typeof openSignup==='function')openSignup('vendor');}} style={{flex:1,background:'#e8c97a',color:'#1a1410',border:'none',borderRadius:8,padding:'14px 0',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Create Account</button>
          </div>
          <div className="form-grid" style={{marginBottom:24}}>
            <div className="form-group"><label>Email Address *</label><input type="email" placeholder="you@email.com" value={form.email} onChange={e=>set('email',e.target.value)} /></div>
            <div className="form-group"><label>Create Password *</label><PasswordInput placeholder="Min 6 characters" value={form.password} onChange={e=>set('password',e.target.value)} /></div>
          </div>
          <hr className="form-divider" />
        </>
      )}
      {authUser && (
        <div style={{background:'#d4f4e0',border:'1px solid #b8e8c8',borderRadius:10,padding:'14px 20px',marginBottom:24,display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:20}}>✓</span>
          <div><div style={{fontWeight:700,fontSize:14,color:'#1a6b3a'}}>Logged in as {authUser.email}</div><div style={{fontSize:12,color:'#2d7a50'}}>Your vendor profile will be linked to this account.</div></div>
        </div>
      )}
      <h2 className="form-section-title"><span className="dot" />Vendor Profile</h2>
      <p style={{ color:'#7a6a5a', marginBottom:32, fontSize:15 }}>
        Join South Jersey's premier vendor network. Get matched with events and hosts looking for exactly what you offer.
      </p>
      <div className="form-grid">
        <div className="form-group"><label>Business Name *</label><input placeholder="e.g. Subtle Boujee" value={form.businessName} onChange={e=>set('businessName',e.target.value)} /></div>
        <div className="form-group"><label>Owner Name *</label><input placeholder="Your full name" value={form.ownerName} onChange={e=>set('ownerName',e.target.value)} /></div>
        {authUser && <div className="form-group"><label>Email Address *</label><input type="email" placeholder="you@email.com" value={form.email} onChange={e=>set('email',e.target.value)} /></div>}
        <div className="form-group"><label>Phone Number</label><input placeholder="(609) 555-0000" value={form.phone} onChange={e=>set('phone',e.target.value)} /></div>
        <ZipInput label="Home Base Zip Code *" value={form.homeZip} onChange={v=>set('homeZip',v)} hint="Your primary location — used to calculate travel distance to events" />
        <div className="form-group"><label>Years in Business</label>
          <select value={form.yearsActive} onChange={e=>set('yearsActive',e.target.value)}>
            <option value="">Select…</option>
            <option value="<1">Less than 1 year</option>
            <option value="1-2">1–2 years</option>
            <option value="3-5">3–5 years</option>
            <option value="6-10">6–10 years</option>
            <option value="10+">10+ years</option>
          </select>
        </div>
        <div className="form-group full"><label>Business Description *</label><textarea placeholder="Tell hosts what makes your business special — what you offer, your style, and what sets you apart." value={form.description} onChange={e=>set('description',e.target.value)} /></div>
      </div>

      <hr className="form-divider" />
      <h3 className="form-section-title"><span className="dot" />Travel Radius</h3>
      <div className="info-box">
        How far will you travel from zip code <strong>{form.homeZip || '—'}</strong> to vend at an event?
        Hosts within this radius will see you as a potential match.
      </div>
      <div className="form-group">
        <label>Max Travel Distance</label>
        <div className="radius-group">
          {RADIUS_OPTIONS.map(r => (
            <button key={r} className={`radius-btn${form.radius===r?' sel':''}`} onClick={()=>set('radius',r)}>{r} miles</button>
          ))}
        </div>
      </div>

      <hr className="form-divider" />
      <div className="form-group">
        <label>Referral Code (optional)</label>
        <input placeholder="e.g. SJVM-K4M2P9" value={form.referralCode} onChange={e=>set('referralCode',e.target.value.toUpperCase().trim())} maxLength={20} />
        <div style={{fontSize:12,color:'#7a6a5a',marginTop:4}}>Were you referred by another vendor? Enter their referral code and you'll both earn $5 in subscription credit.</div>
      </div>

      <div style={{display:'flex',gap:10,marginTop:20}}>
        <button onClick={nextStep} style={{background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'12px 32px',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Next: Categories →</button>
      </div>
      </>)}

      {/* ── STEP 2: Categories ── */}
      {formStep === 2 && (<>
      <h3 className="form-section-title"><span className="dot" />What Type of Listing Is This?</h3>
      <p style={{color:'#7a6a5a',fontSize:14,marginBottom:16}}>Choose one per listing. You can create additional listings for other offerings from your dashboard.</p>
      <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <div onClick={()=>set('vendorType',{market:true,service:false})}
          style={{flex:'1 1 220px',padding:'16px 20px',borderRadius:10,cursor:'pointer',border:`2px solid ${form.vendorType.market?'#c8a850':'#e8ddd0'}`,background:form.vendorType.market?'#fdf9f0':'#fff',transition:'all 0.15s'}}>
          <div style={{fontWeight:700,fontSize:15,color:'#1a1410',marginBottom:4}}>Market Vendor</div>
          <div style={{fontSize:13,color:'#7a6a5a',lineHeight:1.4}}>I sell products at events and pay an event fee</div>
          {form.vendorType.market && <div style={{color:'#c8a850',fontSize:18,marginTop:6}}>&#10003;</div>}
        </div>
        <div onClick={()=>set('vendorType',{market:false,service:true})}
          style={{flex:'1 1 220px',padding:'16px 20px',borderRadius:10,cursor:'pointer',border:`2px solid ${form.vendorType.service?'#c8a850':'#e8ddd0'}`,background:form.vendorType.service?'#fdf9f0':'#fff',transition:'all 0.15s'}}>
          <div style={{fontWeight:700,fontSize:15,color:'#1a1410',marginBottom:4}}>Event Service Provider</div>
          <div style={{fontSize:13,color:'#7a6a5a',lineHeight:1.4}}>I provide a service and get paid by the host</div>
          {form.vendorType.service && <div style={{color:'#c8a850',fontSize:18,marginTop:6}}>&#10003;</div>}
        </div>
      </div>
      <div style={{background:'#fdf9f5',border:'1px solid #e8ddd0',borderRadius:8,padding:'12px 16px',marginBottom:24,fontSize:13,color:'#7a6a5a',lineHeight:1.5}}>
        <strong>Do both?</strong> No problem — create this listing first, then add another from your dashboard. Each listing gets its own categories, photos, and pricing so hosts can find exactly what they need.
      </div>

      {form.vendorType.market && (
        <>
          <h3 className="form-section-title"><span className="dot" />Product Categories</h3>
          <CategorySubcategoryPicker
            categories={form.categories} subcategories={form.subcategories}
            onCategoriesChange={v=>set('categories',v)} onSubcategoriesChange={v=>set('subcategories',v)}
            otherCategory={form.otherCategory} onOtherCategoryChange={v=>set('otherCategory',v)}
            otherSubcategories={otherSubcategories} onOtherSubcategoryChange={(cat,val)=>setOtherSubcategories(p=>{const n={...p};if(val===null)delete n[cat];else n[cat]=val;return n;})}
          />
        </>
      )}

      {form.vendorType.service && (
        <>
          <h3 className="form-section-title" style={{marginTop:form.vendorType.market?24:0}}><span className="dot" />Service Categories</h3>
          <p style={{color:'#7a6a5a',fontSize:14,marginBottom:12}}>Select the types of services you provide.</p>
          <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:16}}>
            {SERVICE_CATEGORIES.map(cat=>(
              <button key={cat} onClick={()=>{
                const has = form.serviceCategories.includes(cat);
                set('serviceCategories', has ? form.serviceCategories.filter(c=>c!==cat) : [...form.serviceCategories, cat]);
                if (has) set('serviceSubcategories', form.serviceSubcategories.filter(s=>!(SERVICE_SUBCATEGORIES[cat]||[]).includes(s)));
              }} style={{
                padding:'8px 16px',borderRadius:20,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif',
                border:'1.5px solid',transition:'all 0.15s',
                background:form.serviceCategories.includes(cat)?'#c8a850':'#fff',
                color:form.serviceCategories.includes(cat)?'#1a1410':'#7a6a5a',
                borderColor:form.serviceCategories.includes(cat)?'#c8a850':'#e8ddd0',
              }}>{cat}</button>
            ))}
          </div>
          {form.serviceCategories.map(cat=>(
            <div key={cat} style={{marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:700,color:'#1a1410',marginBottom:6}}>{cat}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {(SERVICE_SUBCATEGORIES[cat]||[]).map(sub=>(
                  <button key={sub} onClick={()=>{
                    const has = form.serviceSubcategories.includes(sub);
                    set('serviceSubcategories', has ? form.serviceSubcategories.filter(s=>s!==sub) : [...form.serviceSubcategories, sub]);
                  }} style={{
                    padding:'5px 12px',borderRadius:16,fontSize:12,cursor:'pointer',fontFamily:'DM Sans,sans-serif',
                    border:'1px solid',
                    background:form.serviceSubcategories.includes(sub)?'#1a1410':'#fff',
                    color:form.serviceSubcategories.includes(sub)?'#e8c97a':'#5a4a3a',
                    borderColor:form.serviceSubcategories.includes(sub)?'#1a1410':'#e8ddd0',
                  }}>{sub}</button>
                ))}
              </div>
              {form.serviceSubcategories.includes('Other') && (SERVICE_SUBCATEGORIES[cat]||[]).includes('Other') && (
                <input placeholder={`Describe your ${cat} service...`}
                  value={(form.serviceOtherText||{})[cat]||''}
                  onChange={e=>set('serviceOtherText',{...(form.serviceOtherText||{}), [cat]:e.target.value})}
                  style={{width:'100%',marginTop:8,border:'1.5px solid #c8a84b',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'DM Sans,sans-serif',boxSizing:'border-box',outline:'none',background:'#fdf9f5'}} />
              )}
            </div>
          ))}

          {/* Service provider rate & details — inside the service type flow */}
          <div style={{background:'#fdf9f5',border:'1px solid #e8ddd0',borderRadius:10,padding:'16px 20px',marginTop:16,marginBottom:8}}>
            <div style={{fontFamily:'Playfair Display,serif',fontSize:16,color:'#1a1410',marginBottom:4}}>Your Rate & Availability</div>
            <div style={{fontSize:11,color:'#a89a8a',marginBottom:12}}>Your rate is shared with hosts for reference — all payments are handled directly between you and the host.</div>
            <div className="form-grid" style={{gap:10}}>
              <div className="form-group"><label>Minimum Booking Duration</label>
                <select value={form.minBookingDuration} onChange={e=>set('minBookingDuration',e.target.value)}>
                  {SERVICE_DURATIONS.map(d=><option key={d}>{d}</option>)}
                </select>
                {form.minBookingDuration==='Other' && <input placeholder="Describe your availability..." value={form.minBookingDurationOther||''} onChange={e=>set('minBookingDurationOther',e.target.value)} style={{marginTop:6,width:'100%',border:'1.5px solid #c8a84b',borderRadius:8,padding:'8px 12px',fontSize:13,fontFamily:'DM Sans,sans-serif',boxSizing:'border-box',outline:'none',background:'#fdf9f5'}} />}
              </div>
              <div className="form-group"><label>Rate Type</label>
                <select value={form.serviceRateType} onChange={e=>set('serviceRateType',e.target.value)}>
                  <option value="fixed">Fixed Rate</option>
                  <option value="range">Rate Range</option>
                  <option value="quote">Quote Based</option>
                </select>
              </div>
              {form.serviceRateType==='fixed' && <div className="form-group"><label>Your Rate</label><input placeholder="e.g. $500/event" value={form.serviceRateMin} onChange={e=>set('serviceRateMin',e.target.value)} /></div>}
              {form.serviceRateType==='range' && <>
                <div className="form-group"><label>Starting At</label><input placeholder="e.g. $200" value={form.serviceRateMin} onChange={e=>set('serviceRateMin',e.target.value)} /></div>
                <div className="form-group"><label>Up To</label><input placeholder="e.g. $800" value={form.serviceRateMax} onChange={e=>set('serviceRateMax',e.target.value)} /></div>
              </>}
            </div>
            <div className="form-group" style={{marginTop:8}}><label>What You Offer</label><textarea placeholder="Describe your service — style, equipment provided, what's included..." value={form.serviceDescription} onChange={e=>set('serviceDescription',e.target.value)} style={{minHeight:60}} /></div>
          </div>
        </>
      )}

      <hr className="form-divider" />
      <h3 className="form-section-title"><span className="dot" />Event Fit</h3>
      <CheckboxGroup label="Event Types You're Open To" options={EVENT_TYPES} selected={form.eventTypes} onChange={v=>set('eventTypes',v)} otherValue={form.otherEventType} onOtherChange={v=>set('otherEventType',v)} />

      {/* Booth & Logistics — only for market vendors */}
      {form.vendorType.market && (
        <>
          <hr className="form-divider" />
          <h3 className="form-section-title"><span className="dot" />Booth & Logistics</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Do You Carry Liability Insurance?</label>
              <select value={form.insurance?'yes':'no'} onChange={e=>set('insurance',e.target.value==='yes')}>
                <option value="no">No — I do not carry liability insurance</option>
                <option value="yes">Yes — I have a certificate of insurance (COI)</option>
              </select>
              <div style={{fontSize:12,color:'#7a6a5a',marginTop:4}}>Many events require insured vendors. This shows as a badge on your profile.</div>
            </div>
            <div className="form-group"><label>Max Event Fee You'll Pay</label>
              <select value={form.priceMax} onChange={e=>set('priceMax',+e.target.value)}>
                <option value={0}>Free / No fee</option>
                <option value={25}>$25/day</option><option value={50}>$50/day</option><option value={75}>$75/day</option>
                <option value={100}>$100/day</option><option value={125}>$125/day</option><option value={150}>$150/day</option>
                <option value={200}>$200/day</option><option value={250}>$250/day</option><option value={300}>$300/day</option>
                <option value={500}>$500/day</option><option value={1000}>$1,000/day</option>
              </select>
              <div style={{fontSize:11,color:'#a89a8a',marginTop:4}}>Event fees are paid directly to the event host — not through this platform.</div>
            </div>
            <div className="form-group">
              <label>Minimum Purchase Requirement?</label>
              <select value={form.hasMinPurchase?'yes':'no'} onChange={e=>set('hasMinPurchase',e.target.value==='yes')}>
                <option value="no">No minimum purchase</option>
                <option value="yes">Yes — I require a minimum</option>
              </select>
              {form.hasMinPurchase && <div style={{marginTop:8}}><div style={{fontSize:12,color:'#7a6a5a',marginBottom:4}}>Minimum per customer</div><select value={form.minPurchaseAmt} onChange={e=>set('minPurchaseAmt',+e.target.value)}>{[10,15,20,25,30,40,50,75,100].map(a=><option key={a} value={a}>${a}</option>)}</select></div>}
            </div>
            <div className="form-group"><label>Setup Time Needed</label>
              <select value={form.setupTime} onChange={e=>set('setupTime',+e.target.value)}>
                <option value={10}>10 minutes</option><option value={15}>15 minutes</option><option value={20}>20 minutes</option>
                <option value={30}>30 minutes</option><option value={45}>45 minutes</option><option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option><option value={120}>2 hours</option>
              </select>
            </div>
            <div className="form-group"><label>Table / Space Size</label><select value={form.tableSize} onChange={e=>set('tableSize',e.target.value)}><option>6ft</option><option>8ft</option><option>10x10 tent</option><option>10x20 tent</option><option>Flexible</option></select></div>
            <div className="form-group"><label>Need Electrical Access?</label><select value={form.needsElectric?'yes':'no'} onChange={e=>set('needsElectric',e.target.value==='yes')}><option value="no">No</option><option value="yes">Yes</option></select></div>
          </div>
        </>
      )}

      {/* Insurance & Logistics for service providers who aren't market vendors */}
      {form.vendorType.service && !form.vendorType.market && (
        <>
          <hr className="form-divider" />
          <h3 className="form-section-title"><span className="dot" />Insurance & Logistics</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Do You Carry Liability Insurance?</label>
              <select value={form.insurance?'yes':'no'} onChange={e=>set('insurance',e.target.value==='yes')}>
                <option value="no">No — I do not carry liability insurance</option>
                <option value="yes">Yes — I have a certificate of insurance (COI)</option>
              </select>
              <div style={{fontSize:12,color:'#7a6a5a',marginTop:4}}>Many events require insured service providers. This shows as a badge on your profile.</div>
            </div>
            <div className="form-group"><label>Setup Time Needed</label>
              <select value={form.setupTime} onChange={e=>set('setupTime',+e.target.value)}>
                <option value={10}>10 minutes</option><option value={15}>15 minutes</option><option value={20}>20 minutes</option>
                <option value={30}>30 minutes</option><option value={45}>45 minutes</option><option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option><option value={120}>2 hours</option>
              </select>
            </div>
            <div className="form-group"><label>Space Needed</label><select value={form.tableSize} onChange={e=>set('tableSize',e.target.value)}><option>6ft</option><option>8ft</option><option>10x10 tent</option><option>10x20 tent</option><option>Flexible</option></select></div>
            <div className="form-group"><label>Need Electrical Access?</label><select value={form.needsElectric?'yes':'no'} onChange={e=>set('needsElectric',e.target.value==='yes')}><option value="no">No</option><option value="yes">Yes</option></select></div>
          </div>
        </>
      )}

      <div style={{display:'flex',gap:10,marginTop:20}}>
        <button onClick={prevStep} style={{background:'#f5f0ea',color:'#1a1410',border:'1px solid #e0d5c5',borderRadius:8,padding:'12px 24px',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>← Back</button>
        <button onClick={nextStep} style={{background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'12px 32px',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Next: Photos & Links →</button>
      </div>
      </>)}

      {/* ── STEP 3: Photos & Links ── */}
      {formStep === 3 && (<>
      <h3 className="form-section-title"><span className="dot" />Communication & Availability</h3>
      <div className="form-grid">


        <div className="form-group">
          <label>Typical Response Time</label>
          <select value={form.responseTime} onChange={e=>set('responseTime',e.target.value)}>
            <option value="fewhours">Within a few hours</option>
            <option value="24hrs">Within 24 hours</option>
            <option value="48hrs">Within 48 hours</option>
            <option value="weekends">Weekends only</option>
            <option value="flexible">Flexible</option>
          </select>
        </div>

        <div className="form-group">
          <label>Booking Lead Time Needed</label>
          <select value={form.bookingLeadTime} onChange={e=>set('bookingLeadTime',e.target.value)}>
            <option value="1week">At least 1 week</option>
            <option value="2weeks">At least 2 weeks</option>
            <option value="1month">At least 1 month</option>
            <option value="2months">2+ months</option>
            <option value="flexible">Flexible — last minute is fine</option>
          </select>
        </div>

        <div className="form-group">
          <label>How Often Do You Want to Vend?</label>
          <select value={form.eventFrequency} onChange={e=>set('eventFrequency',e.target.value)}>
            <option value="fewyear">A few times a year</option>
            <option value="monthly">About once a month</option>
            <option value="biweekly">2–3 times a month</option>
            <option value="weekly">Weekly</option>
            <option value="asmany">As many as possible</option>
            <option value="flexible">Flexible</option>
          </select>
        </div>

        <div className="form-group">
          <label>New Event Match Notifications</label>
          <select value={form.emailFrequency} onChange={e=>set('emailFrequency',e.target.value)}>
            <option value="instant">Immediately when a matching opportunity is posted</option>
            <option value="weekly">Weekly roundup of new opportunities</option>
            <option value="none">Don't email me — I'll check manually</option>
          </select>
          <div style={{fontSize:12,color:'#7a6a5a',marginTop:4}}>When new events matching your categories and location are posted, how would you like to be notified?</div>
        </div>

      </div>

      <hr className="form-divider" />
      <h3 className="form-section-title"><span className="dot" />Photos & Documents</h3>

      {/* Photo uploads with preview */}
      <div style={{marginBottom:20}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <label style={{fontSize:14,fontWeight:600,color:'#1a1410'}}>Business Photos</label>
          <span style={{fontSize:12,color: photoFiles.length >= 6 ? '#1a6b3a' : '#a89a8a',fontWeight:600}}>{photoFiles.length} of 6 photos</span>
        </div>
        {photoFiles.length > 0 && (
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
            {photoFiles.map((f,i)=>(
              <div key={i} style={{position:'relative',width:90,height:90}}>
                <img src={URL.createObjectURL(f)} alt={`Photo ${i+1}`} style={{width:90,height:90,objectFit:'cover',borderRadius:8,border:'1px solid #e8ddd0'}} />
                <button onClick={()=>setPhotoFiles(p=>p.filter((_,j)=>j!==i))} style={{position:'absolute',top:-6,right:-6,width:20,height:20,borderRadius:'50%',background:'#c62828',color:'#fff',border:'none',fontSize:12,cursor:'pointer',lineHeight:'20px',textAlign:'center',padding:0}}>x</button>
              </div>
            ))}
          </div>
        )}
        {photoFiles.length < 6 && (
          <UploadZone label="Add Photos" accept="image/*" hint={`JPG, PNG — ${6 - photoFiles.length} more allowed`} multiple onChange={files => setPhotoFiles(p => [...p, ...files].slice(0,6))} />
        )}
      </div>

      <div className="form-grid">
        {form.insurance && (
          <div className="form-group"><label>Certificate of Insurance</label><UploadZone label="Insurance COI" accept=".pdf,image/*" hint="PDF or image — required for many events" onChange={file => setCoiFile(file)} /></div>
        )}
        <div className="form-group full"><label>Price Menu / Lookbook (Optional)</label><UploadZone label="Price Sheet / Lookbook" accept=".pdf,image/*" hint="PDF or image — helps hosts understand your offerings" onChange={file => setLookbookFile(file)} /></div>
      </div>

      <hr className="form-divider" />
      <h3 className="form-section-title"><span className="dot" />Videos & Social Links</h3>
      <p style={{color:'#7a6a5a',fontSize:14,marginBottom:16}}>Link your social profiles so hosts can see your work. No file uploads needed — just paste your links.</p>
      <div className="form-grid">
        <div className="form-group"><label>Website</label><input placeholder="https://yourwebsite.com" value={form.website} onChange={e=>set('website',e.target.value)} /></div>
        <div className="form-group"><label>Instagram</label><input placeholder="https://instagram.com/yourbusiness" value={form.instagram} onChange={e=>set('instagram',e.target.value)} /></div>
        <div className="form-group"><label>Facebook</label><input placeholder="https://facebook.com/yourbusiness" value={form.facebook} onChange={e=>set('facebook',e.target.value)} /></div>
        <div className="form-group"><label>TikTok</label><input placeholder="https://tiktok.com/@yourbusiness" value={form.tiktok} onChange={e=>set('tiktok',e.target.value)} /></div>
        <div className="form-group"><label>YouTube</label><input placeholder="https://youtube.com/@yourchannel" value={form.youtube||''} onChange={e=>set('youtube',e.target.value)} /></div>
        <div className="form-group"><label>Other Link</label><input placeholder="Etsy, Pinterest, Yelp, etc." value={form.otherSocial} onChange={e=>set('otherSocial',e.target.value)} /></div>
      </div>

      <div style={{display:'flex',gap:10,marginTop:20}}>
        <button onClick={prevStep} style={{background:'#f5f0ea',color:'#1a1410',border:'1px solid #e0d5c5',borderRadius:8,padding:'12px 24px',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>← Back</button>
        <button onClick={nextStep} style={{background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'12px 32px',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Next: Review & Submit →</button>
      </div>
      </>)}

      {/* ── STEP 4: Review & Submit ── */}
      {formStep === 4 && (<>
      <h3 className="form-section-title"><span className="dot" />Review & Submit</h3>
      <div style={{background:'#fdf9f5',border:'1px solid #e8ddd0',borderRadius:10,padding:16,marginBottom:20}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 16px',fontSize:13}}>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Business:</span> {form.businessName || '—'}</div>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Contact:</span> {form.ownerName || '—'}</div>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Email:</span> {form.email || '—'}</div>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Location:</span> Zip {form.homeZip || '—'} ({form.radius}mi)</div>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Type:</span> {form.vendorType?.service?'Service Provider':'Market Vendor'}</div>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Categories:</span> {[...(form.categories||[]),...(form.serviceCategories||[])].join(', ') || '—'}</div>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Photos:</span> {photoFiles.length} uploaded</div>
          <div><span style={{color:'#a89a8a',fontWeight:600}}>Insurance:</span> {form.insurance ? 'Yes' : 'No'}</div>
        </div>
        <button onClick={()=>setFormStep(1)} style={{marginTop:10,background:'none',border:'none',color:'#c8a84b',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>← Edit any section</button>
      </div>

      {showTos && <TosModal onClose={()=>setShowTos(false)} />}
      <div className="form-submit">
        <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer', marginBottom:16, textAlign:'left', textTransform:'none', letterSpacing:0, fontWeight:400, fontSize:14, color:'#4a3a28' }}>
          <input type="checkbox" checked={tosAgreed} onChange={e=>setTosAgreed(e.target.checked)} style={{ width:18, height:18, marginTop:2, flexShrink:0, display:'block' }} />
          <span>I agree to the <button type="button" onClick={()=>setShowTos(true)} style={{ background:'none', border:'none', color:'#c8a84b', fontWeight:600, cursor:'pointer', textDecoration:'underline', padding:0, fontSize:14, fontFamily:'DM Sans, sans-serif' }}>South Jersey Vendor Market Terms of Service &amp; Non-Circumvention Agreement</button>. I understand that contacting or booking hosts discovered through this platform outside of South Jersey Vendor Market within 12 months is prohibited and subject to a finder's fee.</span>
        </label>
        <button className="btn-submit" disabled={submitting} onClick={async ()=>{ if(!tosAgreed){alert("Please agree to the Terms of Service to continue.");return;} localStorage.removeItem(VENDOR_DRAFT_KEY); localStorage.removeItem(VENDOR_DRAFT_SUBS_KEY); setSubmitting(true); try { await onSubmit(form, { photoFiles, coiFile, lookbookFile }); } catch(e) { console.error('Vendor submit error:', e); alert('Something went wrong. Please try again.'); } setSubmitting(false); }} style={{ opacity: tosAgreed&&!submitting?1:0.5 }}>{submitting ? 'Submitting…' : 'Submit Vendor Profile →'}</button>
        <p style={{ fontSize:13, color:'#a89a8a', marginTop:12 }}>Your profile will be reviewed within 24 hours. Free during beta — <strong style={{ color:'#e8c97a' }}>$15/month</strong> once billing activates.</p>
        <button onClick={prevStep} style={{marginTop:10,background:'none',border:'1px solid #e0d5c5',color:'#7a6a5a',borderRadius:6,padding:'8px 16px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>← Back to Photos</button>
      </div>
      </>)}
    </div>
  );
}

export default VendorForm;
